import React, { useEffect } from "react";
import {
  Box,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import Link from "next/link";
import withRouter, { WithRouterProps } from "next/dist/client/with-router";
import Logo from '../../../public/images/logo-new.svg';

import {
  CustomNavigationBar,
  LinksContainer,
  NavigationBarContainer,
  HeaderContainer,
  ChildrenContainer,
  ShowMobile,
  HideMobile,
} from "./NavigationBar.styles";
import { isViewAsActive, stopViewAs } from "../../utils/auth";
import { AccountMenu, Button, LoginRegisterButtons } from "../"
import { Icon } from "../../icons";
import { useAuthContext } from "src/contexts/AuthContext";
import { colors } from "src/tokens";
import { doesUserHaveAnyAllocationsToManage, isAdmin, UserInterface } from "src/models/User";


interface ResponsiveAppBarProps extends WithRouterProps {
  variant?: "default" | "withProgressBar";
  showMenu?: boolean;
  showLoginButton?: boolean;
  showRegisterButton?: boolean;
  showHomeIconOnMobile?: boolean;
  children?: React.ReactNode;
  withoutUser?: boolean;
}

export interface MenuItemInterface {
  title: string;
  route: string;
  isHidden: boolean;
}

export function getInitials(firstName: string | undefined, lastName: string | undefined): string {
  const firstInitial = firstName ? firstName[0].toUpperCase() : "";
  const lastInitial = lastName ? lastName[0].toUpperCase() : "";
  return (firstInitial + lastInitial).toUpperCase();
}

const NavigationBar = ({
  variant = "default",
  showMenu = true,
  showLoginButton = true,
  showRegisterButton = true,
  showHomeIconOnMobile = false,
  router,
  children,
  withoutUser,
  ...rest
}: ResponsiveAppBarProps) => {
  const { user } = useAuthContext();
  const isViewAs = isViewAsActive();

  const [menuItems, setMenuItems] = React.useState<MenuItemInterface[]>()
  const [loggedUser, setLoggedUser] = React.useState<UserInterface | null | undefined>()
  const [shouldShowMenu, setShouldShowMenu] = React.useState<boolean>(true);

  useEffect(() => {
    setMenuItems([
      { title: "Shows", route: "/shows", isHidden: !isAdmin(user) },
      { title: "Shows (Audience View)", route: "/audience", isHidden: !isAdmin(user) },
      { title: "Shows", route: "/audience", isHidden: isAdmin(user) },
      { title: "Allocations", route: "/allocations/my-allocations", isHidden: !isAdmin(user) },
      { title: "Allocations V2", route: "/allocations-v2/my-allocations", isHidden: !(doesUserHaveAnyAllocationsToManage(user) || isAdmin(user)) },
      { title: "Users", route: "/users", isHidden: !isAdmin(user) },
      { title: "Rules", route: "/rules", isHidden: !isAdmin(user) },
    ])
    setLoggedUser(user);
  }, [user])

  useEffect(() => {
    setShouldShowMenu(showMenu);
  }, [showMenu])

  useEffect(() => {
    if (window.location.hash) {
      const anchor = window.location.hash;

      const targetElement = document.getElementById(anchor);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [router.asPath]);

  const smallScreen = useMediaQuery('(max-width:600px)');

  const isUnderRoot = (routePart: string): string => {
    return router.asPath.split('/').some(part => part === routePart.split('/')[1])
      ? colors["lime.500"]
      : colors["neutral.50"]
  }

  return (
    <CustomNavigationBar
      position="static"
      {...rest}
    >
      <NavigationBarContainer maxWidth={false}>
        <Toolbar disableGutters>
          <HeaderContainer>
            {/* <LogoLink href="/" data-cy="navbar-home-button"> //temporary */}
            {showHomeIconOnMobile ? (
              <>
                <ShowMobile>
                  <Icon icon="Home" />
                </ShowMobile>
                <HideMobile>
                  <Logo />
                </HideMobile>
              </>
            ) : <Logo />}
            {/* </LogoLink> */}
            {shouldShowMenu &&
              <LinksContainer>
                {menuItems?.filter(item => !item.isHidden)
                  .map((page) => (
                    <Link href={page.route} key={page.title}>
                      <Button variant="tertiary" sx={{ width: 'max-content', color: isUnderRoot(page.route) }}>
                        {page.title}
                      </Button>
                    </Link>
                  ))}
              </LinksContainer>
            }
          </HeaderContainer>
          {children && (
            <ChildrenContainer>{children}</ChildrenContainer>
          )}
          {
            typeof loggedUser === "undefined" && !withoutUser
              ? <LoginRegisterButtons showLoginButton={showLoginButton} showRegisterButton={showRegisterButton} />
              : <Box data-cy="login-status" sx={{ display: "flex", justifyContent: "flex-end", flex: smallScreen && !loggedUser?.email ? 3 : 2 }}>
                {
                  loggedUser?.email || smallScreen
                    ? isViewAs
                      ? <Box sx={{ display: "flex" }}>
                        <Box
                          data-cy="navbar-username"
                          sx={{ p: 0, cursor: "pointer" }}
                          style={{ color: "orange" }}
                        >{(loggedUser?.firstName && loggedUser?.lastName) ?
                          `Viewing as ` +
                          `${loggedUser?.firstName} ${loggedUser?.lastName}` :
                          `Guest User - ${loggedUser?.email}`}
                        </Box>
                        <Box sx={{ px: "10px" }}>|</Box>
                        <Box
                          onClick={() => stopViewAs()}
                          sx={{ p: 0, cursor: "pointer" }}
                        >
                          Stop view as
                        </Box>
                      </Box>
                      : <AccountMenu user={loggedUser} menuItems={menuItems} showLoginButton={showLoginButton} showRegisterButton={showRegisterButton} memberScore={10000} />
                    : <></>
                }
              </Box>
          }
        </Toolbar>
      </NavigationBarContainer>
    </CustomNavigationBar>
  );
};

export interface NavigationBarProps extends React.ComponentProps<typeof NavigationBar> {
  as?: React.ElementType;
}

NavigationBar.displayName = "NavigationBar";

const ComponentWithRouter = withRouter(NavigationBar)

export { ComponentWithRouter as NavigationBar };